import {Dispatch, SetStateAction, useContext, useMemo, useState} from 'react'

import {isLocalProfile} from '@web/core/config'

import {
    ModalContextType,
    ModalKey,
    ModalValue,
    ModalDataMap,
    ModalStoreType,
    ModalProps,
    ModalRegisterValue,
    ModalComponentType,
    SetModalRegisterType,
} from '../interfaces'

import {ModalContext} from './context'

/**
 * 모달 등록 정보를 Store에 추가하는 함수
 * @param store - 모달을 저장할 Map 객체
 * @returns 모달 등록 배열을 처리하는 함수
 */
const updateModalMap =
    (store: ModalStoreType): SetModalRegisterType =>
    (modalDataMaps) => {
        try {
            modalDataMaps.forEach((item) => {
                // 배열 형식: [모달 객체, 옵션]
                if (Array.isArray(item)) {
                    const modalData = item[0]
                    const options = item[1]
                    Object.entries(modalData).forEach(([key, value]) => {
                        store.set(`${key}`, {
                            component: value,
                            options,
                        })
                    })
                } else {
                    // 객체 형식: 직접 모달 정보
                    Object.entries(item).forEach(([key, value]) => {
                        store.set(`${key}`, value)
                    })
                }
            })
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`ModalProvider: Error creating ModalProvider`, e)
        }
    }

/** 등록되지 않은 모달 키 사용 시 개발자 경고 출력 */
const handleWarning = (key: string) => {
    if (isLocalProfile) {
        // eslint-disable-next-line no-console
        console.warn(`ModalProvider 에 등록된 key 가 없습니다.`, key)
    }
}

/**
 * 모달 관리 객체를 생성하는 함수
 * 모달 열기, 닫기, 데이터 조회 등의 기능 제공
 * @param updateModals - 열린 모달 목록 상태 업데이트 함수
 * @param modalMap - 등록된 모달 정보를 저장하는 Map
 * @param superModal - 부모 모달 컨텍스트 (중첩된 모달 지원)
 * @returns 모달 제어 함수들을 포함하는 객체
 */
function getModal({
    updateModals,
    modalMap,
    superModal,
}: {
    updateModals: Dispatch<SetStateAction<ModalValue[]>>
    modalMap: ModalStoreType
    superModal: Maybe<ModalContextType['modal']>
}): ModalContextType['modal'] {
    let prev: ModalValue[] = []

    /**
     * 열린 모달 목록 업데이트
     * @param data - 새로운 모달 목록
     */
    const setData = (data: ModalValue[]) => {
        prev = data

        updateModals(data)
    }
    return {
        /**
         * 특정 모달의 데이터 조회
         * @param key - 모달 키
         * @returns 모달에 전달된 데이터
         */
        getData: <T extends ModalProps>(key: ModalKey): T | undefined => {
            if (modalMap.has(key)) {
                return prev.find((m) => m.key === key)?.data as T
            } else if (superModal) {
                return superModal.getData(key)
            }
            return undefined
        },
        /**
         * 특정 모달이 열려있는지 확인
         * @param key - 모달 키
         * @returns 모달 열림 여부
         */
        getOpened: (key: ModalKey): boolean => {
            const result = prev.some((m) => m.key === key)
            if (!result && superModal) {
                return superModal.getOpened(key)
            }
            return result
        },
        /**
         * 모달 열기
         * @param key - 모달 키
         * @param data - 모달에 전달할 데이터 (선택사항)
         */
        open: <T extends ModalProps>(key: ModalKey, data?: T) => {
            if (modalMap.has(key)) {
                setData([...prev.filter((m) => m.key !== key), {key, data: data || {}}])
            } else if (superModal) {
                superModal.open(key, data)
            } else {
                handleWarning(key)
            }
        },
        /**
         * 특정 모달 닫기
         * @param key - 모달 키
         */
        close: (key: ModalKey) => {
            if (modalMap.has(key)) {
                setData(prev.filter((m) => m.key !== key))
            } else if (superModal) {
                superModal.close(key)
            } else {
                handleWarning(key)
            }
        },
        /**
         * 가장 최근 모달 닫기
         * @returns 닫은 모달의 키
         */
        closeLast: (): ModalKey | undefined => {
            if (prev.length) {
                const last = prev.length - 1
                const result = prev[last]
                setData(prev.filter((v) => v !== result))
                return result?.key
            } else if (superModal) {
                return superModal.closeLast()
            }
            return undefined
        },
        /**
         * 모든 모달 닫기
         */
        clean: () => {
            setData([])
            if (superModal) {
                superModal.clean()
            }
        },
    }
}

/**
 * 모달 항목의 컴포넌트 타입 반환
 * @param item - 모달 값 정보
 * @param data - 등록된 모달 정보
 * @returns 렌더링할 모달 컴포넌트 타입 또는 null
 */
function getModalComponentType(item: ModalValue, data?: ModalRegisterValue): Maybe<ModalComponentType> {
    if (data) {
        if ('component' in data) {
            if (!data.options.withModal && data.component) {
                return {
                    ...item,
                    component: data.component,
                }
            }
        } else {
            return {
                ...item,
                component: data,
            }
        }
    }
    return null
}

/**
 * 열린 모달 목록으로부터 렌더링할 모달 컴포넌트 배열 생성
 * 메모이제이션을 통해 의존성 변경 시에만 재계산
 * @param store - 등록된 모달 정보를 저장하는 Map
 * @param openedModals - 현재 열린 모달 목록
 * @returns 렌더링할 모달 컴포넌트 배열
 */
const useOpenedModals = ({
    store,
    openedModals,
}: {
    store: Map<ModalKey, ModalRegisterValue>
    openedModals: ModalValue[]
}): ModalComponentType[] => {
    return useMemo(() => {
        const result: ModalComponentType[] = []
        openedModals.forEach((item) => {
            const value = getModalComponentType(item, store.get(item.key))
            value && result.push(value)
        })

        return result
    }, [store, openedModals])
}

/**
 * 모달 스토어 훅
 * 모달 레지스트리를 관리하고 모달 제어 기능 제공
 * @param modalRegistry - 초기 모달 등록 정보 (선택사항)
 * @returns 모달 맵, 열린 모달 컴포넌트, 모달 컨텍스트 정보
 */
export const useModalStore = ({modalRegistry}: {modalRegistry?: ModalDataMap}) => {
    const [openedModals, updateModals] = useState<ModalValue[]>([])

    const {isSetup: isSetupSuper, modal: superModal} = useContext(ModalContext)

    const modalMap: ModalStoreType = useMemo(() => {
        return new Map()
    }, [])

    return {
        modalMap,
        openedModals: useOpenedModals({
            store: modalMap,
            openedModals,
        }),
        modal: useMemo((): ModalContextType => {
            const initModalRegister = updateModalMap(modalMap)

            modalRegistry && initModalRegister(modalRegistry)

            return {
                isSetup: true,
                setModalRegister: initModalRegister,
                modal: getModal({
                    updateModals,
                    modalMap,
                    superModal: isSetupSuper ? superModal : null,
                }),
            }
            // 한번만 modal 정보를 만들기위해 주석 처리
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []),
    }
}
